import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Save, Send, Camera, Clock, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];

const TEXTY = ['phone','province','home_language','school_club','guardian_name','preferred_position','season_phase','xv_assist_goal','trial_date','current_injury_notes','run_3km_time'];
const ARRAYS = ['other_positions','training_locations','equipment_access'];
const INTS = ['playing_experience_years','training_days_per_week','bench_press_reps','squat_reps','pull_ups_reps','push_ups_1min_reps','sit_ups_2min_reps','sit_ups_1min_reps','bleep_test_level','bleep_test_shuttle'];

const ANTHRO = [
  ['body_mass_kg','Body Mass','kg'], ['height_cm','Height','cm'],
  ['skinfold_triceps_mm','Skinfold — Triceps','mm'], ['skinfold_biceps_mm','Skinfold — Biceps','mm'],
  ['skinfold_subscapular_mm','Skinfold — Subscapular','mm'], ['skinfold_suprailiac_mm','Skinfold — Supra-iliac','mm'],
  ['skinfold_calf_mm','Skinfold — Calf','mm'], ['skinfold_thigh_mm','Skinfold — Thigh','mm'],
  ['skinfold_abdominal_mm','Skinfold — Abdominal','mm'], ['girth_calf_cm','Girth — Calf','cm'],
  ['girth_midthigh_cm','Girth — Mid-thigh','cm'], ['girth_forearm_cm','Girth — Forearm','cm'],
];
const FLEX = [
  ['sit_reach_cm','Sit and Reach','cm'], ['slr_left_deg','Straight Leg Raise (L)','°'], ['slr_right_deg','Straight Leg Raise (R)','°'],
  ['thomas_iliopsoas_left_deg','Thomas — Iliopsoas (L)','°'], ['thomas_iliopsoas_right_deg','Thomas — Iliopsoas (R)','°'],
  ['thomas_rectus_left_deg','Thomas — Rectus Femoris (L)','°'], ['thomas_rectus_right_deg','Thomas — Rectus Femoris (R)','°'],
];
const SPEED = [['sprint_10m_sec','10m Sprint','sec'], ['sprint_40m_sec','40m Sprint','sec'], ['illinois_agility_sec','Illinois Agility','sec']];
const POWER = [['vertical_jump_cm','Vertical Jump','cm'], ['broad_jump_cm','Standing Broad Jump','cm']];

const STATUS_UI = {
  draft: { color: 'bg-amber-500/10 border-amber-500/30 text-amber-300', icon: AlertTriangle, title: 'Profile in Draft', msg: 'Complete your profile below and submit it for approval. Partial saves are supported — you can return anytime.' },
  pending_coach: { color: 'bg-blue-500/10 border-blue-500/30 text-blue-300', icon: Clock, title: 'Pending Coach Approval', msg: 'Your profile will be activated once your coach approves.' },
  pending_parent: { color: 'bg-blue-500/10 border-blue-500/30 text-blue-300', icon: Clock, title: 'Pending Parent / Guardian Approval', msg: 'Your coach has approved. Your parent or guardian must also approve before your profile goes live.' },
  pending_both: { color: 'bg-blue-500/10 border-blue-500/30 text-blue-300', icon: Clock, title: 'Pending Approval', msg: 'Your profile will be activated once your coach approves. Your parent or guardian must also approve before your profile goes live.' },
  active: { color: 'bg-green-500/10 border-green-500/30 text-green-300', icon: CheckCircle2, title: 'Profile Active', msg: 'Your profile is live. Keep your physical testing data up to date each test cycle.' },
  declined: { color: 'bg-red-500/10 border-red-500/30 text-red-300', icon: AlertTriangle, title: 'Changes Requested / Declined', msg: 'Your profile was returned. Review the notes below, correct your information and resubmit.' },
};

function Num({ k, label, unit, form, set, testid }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs">{label} {unit && <span className="text-zinc-600">({unit})</span>}</Label>
      <Input type="number" step="any" value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)}
        className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid={testid || `fp-${k}`} />
    </div>
  );
}

function Chips({ options, value = [], onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${value.includes(o) ? 'bg-primary-neon text-black border-primary-neon' : 'bg-zinc-950 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-950/50">
      <h3 className="font-heading font-bold text-white mb-1">{title}</h3>
      {note && <p className="text-zinc-500 text-xs mb-4">{note}</p>}
      {!note && <div className="mb-4" />}
      {children}
    </div>
  );
}

export default function FullProfileForm({ onChanged }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/player/full-profile');
      setMeta(res.data);
      const p = res.data.profile || {};
      const f = {};
      Object.keys(p).forEach((k) => { if (p[k] !== null && p[k] !== undefined) f[k] = p[k]; });
      setForm(f);
    } catch (e) { console.warn('Full profile not available (non-player or error)', e?.response?.status); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const FIELD_LABELS = {
    body_mass_kg: 'Body Mass (25–200 kg)', height_cm: 'Height (100–230 cm)',
    playing_experience_years: 'Playing Experience (0–35 yrs)', training_days_per_week: 'Training Days (1–7)',
    sprint_10m_sec: '10m Sprint (1–5 sec)', sprint_40m_sec: '40m Sprint (3.5–12 sec)',
    illinois_agility_sec: 'Illinois Agility (10–40 sec)', vertical_jump_cm: 'Vertical Jump (5–120 cm)',
    broad_jump_cm: 'Broad Jump (50–400 cm)', run_3km_time: '3km Time (use mm:ss, e.g. 14:30)',
    bleep_test_level: 'Bleep Test Level (1–23)', bleep_test_shuttle: 'Bleep Test Shuttle (1–16)',
  };

  const humanize = (k) => FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const formatApiError = (e, fallback) => {
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.slice(0, 3).map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : '';
        const msg = (d.msg || '').replace(/^Value error,\s*/i, '');
        return field ? `${humanize(field)}: ${msg}` : msg;
      });
      return `Please check these fields — ${msgs.join(' · ')}`;
    }
    return fallback;
  };

  const buildPayload = () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (['id','user_id','profile_status','profile_photo','data_source','validated_by','validated_at','created_at','updated_at','submitted_at','activated_at','last_consent_sent_at','decline_reason','declined_by','body_fat_pct','muscle_mass_kg','vo2max_estimated','bench_press_1rm_calculated_kg','squat_1rm_calculated_kg','run_3km_time_sec'].includes(k)) return;
      if (ARRAYS.includes(k)) { payload[k] = v || []; return; }
      if (TEXTY.includes(k)) { if (v !== '' && v != null) payload[k] = v; return; }
      if (v === '' || v == null) return;
      payload[k] = INTS.includes(k) ? parseInt(v, 10) : parseFloat(v);
    });
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch('/player/full-profile', buildPayload());
      setMeta(res.data);
      toast.success('Profile progress saved');
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e, 'Failed to save profile')); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.patch('/player/full-profile', buildPayload());
      const res = await api.post('/player/full-profile/submit');
      toast.success(res.data.message || 'Profile submitted for approval');
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e, 'Failed to submit profile')); }
    finally { setSubmitting(false); }
  };

  const handleResend = async () => {
    try {
      const res = await api.post('/player/full-profile/resend');
      toast.success(res.data.message || 'Approval emails resent');
    } catch (e) { toast.error(formatApiError(e, 'Failed to resend')); }
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { toast.error('Only JPG or PNG photos are accepted'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo exceeds the 5 MB limit'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post('/player/full-profile/photo', { filename: file.name, data: reader.result });
        toast.success('Photo uploaded');
        await load();
      } catch (err) { toast.error(err.response?.data?.detail || 'Photo upload failed'); }
    };
    reader.readAsDataURL(file);
  };

  if (!meta) return <Card className="bg-zinc-900 border-zinc-800 p-8 text-zinc-500">Loading profile…</Card>;

  const { age, is_u18, age_gates, options, profile, coach_email, guardian_email } = meta;
  const status = profile.profile_status || 'draft';
  const ui = STATUS_UI[status] || STATUS_UI.draft;
  const Icon = ui.icon;
  const isPending = ['pending_coach', 'pending_parent', 'pending_both'].includes(status);
  const canSubmit = status === 'draft' || status === 'declined';

  return (
    <div className="space-y-6" data-testid="full-profile-form">
      {/* Status banner */}
      <div className={`border rounded-lg p-5 flex items-start gap-3 ${ui.color}`} data-testid="profile-status-banner">
        <Icon className="w-6 h-6 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-heading font-bold">{ui.title}</p>
          <p className="text-sm mt-1 opacity-90">{ui.msg}</p>
          {isPending && (
            <p className="text-xs mt-2 opacity-75" data-testid="pending-recipients">
              Approval request sent to your coach at {coach_email}{is_u18 && guardian_email ? `, and consent request to your parent/guardian at ${guardian_email}` : ''}.
            </p>
          )}
          {status === 'declined' && profile.decline_reason && (
            <p className="text-xs mt-2 bg-black/30 rounded p-2" data-testid="decline-reason">
              {profile.declined_by || 'Approver'} notes: {profile.decline_reason}
            </p>
          )}
        </div>
        {isPending && (
          <Button onClick={handleResend} size="sm" variant="outline" className="border-current text-current bg-transparent shrink-0" data-testid="resend-approvals-button">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Resend
          </Button>
        )}
      </div>

      <Card className="bg-zinc-900 border-zinc-800 p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-heading font-bold">Full Player Profile &amp; Physical Testing</h2>
            <p className="text-zinc-500 text-xs mt-1">
              BokSmart fitness testing protocols · Age group: {age} yrs {is_u18 ? '(Under 18 — parent/guardian consent required)' : ''}
              {profile.data_source === 'coach_validated' && (
                <Badge className="ml-2 bg-green-500/10 text-green-400 border border-green-500/30 text-[10px]" data-testid="coach-validated-badge">
                  Coach-validated{profile.validated_by ? ` by ${profile.validated_by}` : ''}
                </Badge>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {profile.profile_photo
              ? <img src={profile.profile_photo} alt="Profile" className="w-14 h-14 rounded-full object-cover border-2 border-primary-neon" data-testid="profile-photo-preview" />
              : <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center"><Camera className="w-5 h-5 text-zinc-600" /></div>}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" onChange={handlePhoto} className="hidden" data-testid="photo-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="border-zinc-700 text-zinc-300" data-testid="upload-photo-button">
              <Camera className="w-4 h-4 mr-2" />Upload Photo
            </Button>
          </div>
        </div>

        {/* 3.1 Personal & Contact */}
        <Section title="Personal & Contact Information">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs">Phone Number (optional)</Label>
              <Input value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-phone" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Province</Label>
              <Select value={form.province || ''} onValueChange={(v) => set('province', v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-province"><SelectValue placeholder="Select province" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">{SA_PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Home Language</Label>
              <Select value={form.home_language || ''} onValueChange={(v) => set('home_language', v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-home-language"><SelectValue placeholder="Select language" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">{options.home_languages.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">School / Club</Label>
              <Input value={form.school_club ?? ''} onChange={(e) => set('school_club', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-school-club" />
            </div>
            {is_u18 && (
              <div>
                <Label className="text-zinc-400 text-xs">Guardian Name (parent/guardian from sign-up)</Label>
                <Input value={form.guardian_name ?? ''} onChange={(e) => set('guardian_name', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-guardian-name" />
              </div>
            )}
          </div>
        </Section>

        {/* 3.2 Rugby Profile */}
        <Section title="Rugby Profile" note="This data feeds directly into XV Assist to personalise your training programmes.">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs">Preferred Position</Label>
              <Select value={form.preferred_position || ''} onValueChange={(v) => set('preferred_position', v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-preferred-position"><SelectValue placeholder="Select position" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-64">{options.positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Num k="playing_experience_years" label="Playing Experience" unit="years" form={form} set={set} />
            <Num k="training_days_per_week" label="Training Days / Week" unit="1–7" form={form} set={set} />
            <div>
              <Label className="text-zinc-400 text-xs">Current Season Phase</Label>
              <Select value={form.season_phase || ''} onValueChange={(v) => set('season_phase', v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-season-phase"><SelectValue placeholder="Select phase" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">{options.season_phases.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">XV Assist Goal</Label>
              <Select value={form.xv_assist_goal || ''} onValueChange={(v) => set('xv_assist_goal', v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-xv-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">{options.xv_assist_goals.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.xv_assist_goal === 'Prepare for union trials' && (
              <div>
                <Label className="text-zinc-400 text-xs">Trial Date</Label>
                <Input type="date" value={form.trial_date ?? ''} onChange={(e) => set('trial_date', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-trial-date" />
              </div>
            )}
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 text-xs">Other Positions Played</Label>
            <Chips options={options.positions} value={form.other_positions || []} onChange={(v) => set('other_positions', v)} testid="fp-other-positions" />
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 text-xs">Training Location</Label>
            <Chips options={options.training_locations} value={form.training_locations || []} onChange={(v) => set('training_locations', v)} testid="fp-training-locations" />
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 text-xs">Equipment Access</Label>
            <Chips options={options.equipment} value={form.equipment_access || []} onChange={(v) => set('equipment_access', v)} testid="fp-equipment" />
          </div>
          <div className="mt-4">
            <Label className="text-zinc-400 text-xs">Current Injury or Medical Condition (optional — activates an injury-modified programme)</Label>
            <Textarea value={form.current_injury_notes ?? ''} onChange={(e) => set('current_injury_notes', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1 min-h-[70px]" data-testid="fp-injury-notes" />
          </div>
        </Section>

        {/* 3.3 Anthropometry */}
        <Section title="Anthropometric Baseline" note="Enter the most recent measurements taken by your coach, biokineticist or medical professional. Self-reported values are flagged until coach-validated. Body Fat % and Muscle Mass are calculated automatically.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ANTHRO.map(([k, l, u]) => <Num key={k} k={k} label={l} unit={u} form={form} set={set} />)}
          </div>
          {(profile.body_fat_pct != null || profile.muscle_mass_kg != null) && (
            <div className="flex gap-3 mt-4">
              {profile.body_fat_pct != null && <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30" data-testid="derived-body-fat">Body Fat: {profile.body_fat_pct}% (derived — Siri)</Badge>}
              {profile.muscle_mass_kg != null && <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30" data-testid="derived-muscle-mass">Muscle Mass: {profile.muscle_mass_kg} kg (derived — Martin)</Badge>}
            </div>
          )}
        </Section>

        {/* 3.4 Flexibility */}
        <Section title="Flexibility Tests">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {FLEX.map(([k, l, u]) => <Num key={k} k={k} label={l} unit={u} form={form} set={set} />)}
          </div>
        </Section>

        {/* 3.5 + 3.6 Speed / Agility / Power */}
        <Section title="Speed, Agility & Power" note="Best of two attempts (sprints/agility), best of three (jumps).">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...SPEED, ...POWER].map(([k, l, u]) => <Num key={k} k={k} label={l} unit={u} form={form} set={set} />)}
          </div>
        </Section>

        {/* 3.7 Strength — age gated */}
        {(age_gates.bench_multirm || age_gates.bench_1rm || age_gates.squat) && (
          <Section title="Muscle Strength Tests" note="BokSmart: strength tests after speed/power. 1RM Bench from 16+, 5–10RM Bench from 14+, Squat from 18+ only. 1RM equivalents auto-calculated.">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {age_gates.bench_1rm && <Num k="bench_press_1rm_kg" label="Bench Press — 1RM" unit="kg" form={form} set={set} />}
              {age_gates.bench_multirm && <>
                <Num k="bench_press_multirm_kg" label="Bench Press — 5–10RM" unit="kg" form={form} set={set} />
                <Num k="bench_press_reps" label="Bench Press Reps" unit="5–10" form={form} set={set} />
              </>}
              {age_gates.squat && <>
                <Num k="squat_1rm_kg" label="Parallel Squat — 1RM" unit="kg" form={form} set={set} />
                <Num k="squat_multirm_kg" label="Parallel Squat — 5–10RM" unit="kg" form={form} set={set} />
                <Num k="squat_reps" label="Squat Reps" unit="5–10" form={form} set={set} />
              </>}
            </div>
            <div className="flex gap-3 mt-4">
              {profile.bench_press_1rm_calculated_kg != null && <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30" data-testid="derived-bench-1rm">Bench 1RM equivalent: {profile.bench_press_1rm_calculated_kg} kg</Badge>}
              {profile.squat_1rm_calculated_kg != null && <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30" data-testid="derived-squat-1rm">Squat 1RM equivalent: {profile.squat_1rm_calculated_kg} kg</Badge>}
            </div>
          </Section>
        )}

        {/* 3.8 + 3.9 Endurance & Cardio — age gated */}
        <Section title="Muscle Endurance & Cardiorespiratory Fitness" note="VO₂max is estimated automatically from your Bleep Test result.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {age_gates.pull_ups && <Num k="pull_ups_reps" label="Pull-Ups (max reps)" unit="reps" form={form} set={set} />}
            {age_gates.flexed_arm_hang && <Num k="flexed_arm_hang_sec" label="Flexed Arm Hang" unit="sec" form={form} set={set} />}
            <Num k="push_ups_1min_reps" label="Push-Ups (1 min)" unit="reps" form={form} set={set} />
            {age_gates.sit_ups_2min && <Num k="sit_ups_2min_reps" label="Sit-Ups (2 min)" unit="reps" form={form} set={set} />}
            {age_gates.sit_ups_1min && <Num k="sit_ups_1min_reps" label="Sit-Ups (1 min)" unit="reps" form={form} set={set} />}
            <Num k="bleep_test_level" label="Bleep Test — Level" unit="level" form={form} set={set} />
            <Num k="bleep_test_shuttle" label="Bleep Test — Shuttle" unit="shuttle" form={form} set={set} />
            <div>
              <Label className="text-zinc-400 text-xs">3km Time Trial <span className="text-zinc-600">(mm:ss)</span></Label>
              <Input value={form.run_3km_time ?? ''} onChange={(e) => set('run_3km_time', e.target.value)} placeholder="14:30" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" data-testid="fp-run-3km" />
            </div>
            <Num k="sprint_5m_shuttle_total_distance_m" label="5m Shuttle Total Distance" unit="m" form={form} set={set} />
          </div>
          {profile.vo2max_estimated != null && (
            <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30 mt-4" data-testid="derived-vo2max">VO₂max estimate: {profile.vo2max_estimated} ml/kg/min</Badge>
          )}
        </Section>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} variant="outline" className="border-zinc-700 text-zinc-200 px-6" data-testid="save-progress-button">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Progress'}
          </Button>
          {canSubmit && (
            <Button onClick={handleSubmit} disabled={submitting} className="btn-primary px-6" data-testid="submit-for-approval-button">
              <Send className="w-4 h-4 mr-2" />{submitting ? 'Submitting…' : status === 'declined' ? 'Resubmit for Approval' : 'Submit for Approval'}
            </Button>
          )}
          {isPending && <p className="text-zinc-500 text-xs self-center">You can continue updating your information while you wait for approval.</p>}
        </div>
      </Card>
    </div>
  );
}
